/** 1. Get params from URL:
 *   - emailAddress
 *   - intent
 *   - next
 *   - code
 *   - _branch_match_id
 *   1.b) if missing params, display an error. (test)
 * 2. Call `accountLoginByEmail` with email_address from emailAddress and email_authentication_code from code (test)
 * 3. If accountLoginByEmail was succesful, forward to `next` (test)
 *   3.b) else, show error (test).
 */

import React, { useEffect, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'react-apollo';
import { withRouter } from 'react-router-dom';
import qs from 'query-string';
import compose from 'lodash.flowright';
import { connect } from 'react-redux';
import moment from 'moment';

import { MeQuery } from '../../../../graphql/queries/account';
import {
  accountAddRoles,
  accountLoginByEmail as accountLoginByEmailMutation,
  accountUpdate as accountUpdateMutation,
  clientStoreSet as clientStoreSetMutation,
} from '../../../../graphql/mutations/account';
import { getErrorString } from '../../../../util/normalize-errors';
import ErrorView from '../../../../components/global/ErrorView';
import { loginSuccess as loginSuccessAction } from '../../../../actions/auth-actions';
import ACCOUNT_ROLES from '../../../../constants/account-roles';
import { ACCOUNT_REFERRAL_TYPE } from '../../../../constants/account-referrals';
import { getInvitiationDetailsFromSearchQuery } from '../../../pro/views/InvitationResolver/helpers';
import useStoreReferralAttributes from '../../../../hooks/useStoreReferralAttributes';
import { isProAccount } from '../../../../util/account';
import FullScreenLoader from '../../../../components/global/FullScreenLoader';

const propTypes = {
  accountLoginByEmail: PropTypes.func.isRequired,
  accountUpdate: PropTypes.func.isRequired,
  addShutterbugRole: PropTypes.func.isRequired,
  contributorAssignmentShare: PropTypes.shape({
    album: PropTypes.shape({
      id: PropTypes.string,
    }),
    album_folder: PropTypes.shape({
      id: PropTypes.string,
    }),
  }),
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  loginSuccess: PropTypes.func.isRequired,
  user: PropTypes.shape({
    id: PropTypes.string,
  }),
  clientStoreSet: PropTypes.func,
};

const EmailAuthLinkResolver = ({
  accountLoginByEmail,
  accountUpdate,
  addShutterbugRole,
  clientStoreSet,
  // Set this true by default as the query runs asynchronously,
  // Avoids the blocking modal rendering and then immediately hiding
  // when this starts as false and AccountRefIdExists resolves to 'true'
  // post-first render.
  history,
  location,
  loginSuccess,
  user,
}) => {
  const contributorInvitationDetails = getInvitiationDetailsFromSearchQuery(
    location,
  );
  const { code, emailAddress, next } = qs.parse(location?.search);
  // User registration details passed through url to us.
  const { firstname, lastname, username } = qs.parse(next);
  const [errorText, setErrorText] = useState('');
  const {
    accountReferralCreate,
    referralAttributesParsed,
    hasActiveReferralAttribute,
  } = useStoreReferralAttributes();

  // Referral details passed through url to us.

  const authByEmail = async () => {
    setErrorText('');

    if (!emailAddress || !code) {
      setErrorText(
        'Sorry, part of your link seems to be missing, please click the link in your email or copy and paste it exactly.',
      );
    } else {
      try {
        await accountLoginByEmail(emailAddress, code);

        if (accountReferralCreate && hasActiveReferralAttribute) {
          await accountReferralCreate(ACCOUNT_REFERRAL_TYPE.ACCOUNT_SIGNUP);
        }
        // Trigger re-running of user query, so we can continue in [user] effect.
        loginSuccess();
      } catch (err) {
        setErrorText(getErrorString(err));
      }
    }
  };

  useEffect(() => {
    if (referralAttributesParsed) {
      authByEmail();
    }
  }, [emailAddress, code, referralAttributesParsed]);

  const updateUserRedirect = async () => {
    // Optionally update account with initial registration details
    if (firstname && lastname && username) {
      await accountUpdate({
        id: user.id,
        username,
        name: `${firstname} ${lastname}`,
      });
    }

    const shouldAddShutterbugRole = !isProAccount(user);

    if (
      shouldAddShutterbugRole &&
      !user.roles.includes(ACCOUNT_ROLES.SHUTTERBUG)
    ) {
      await addShutterbugRole(user.id);
    }

    // Set the time the user signed up, and forget (no need to await).
    clientStoreSet?.({
      account_id: user.id,
      key: 'shutterbug_signup_timestamp',
      data: moment().valueOf().toString(),
    });

    // Set contributor, if we got contrib details in mapStateToProps.
    if (contributorInvitationDetails) {
      // Special case here. The contributor invitation details are parsed from the `/next` URl,
      // So we must parse stringify them for the invitation resolver to use.
      history.push(
        `/contributors/invres?${qs.stringify(contributorInvitationDetails)}`,
      );

      return;
    }

    // Redirect in to app, unless there is a contrib entity to go to, or preset (next) redirect URL.
    history.replace(next ?? '/app');
  };

  useEffect(() => {
    if (user && referralAttributesParsed) {
      updateUserRedirect();
    }
  }, [user, referralAttributesParsed]);

  return (
    <Fragment>
      {!errorText && <FullScreenLoader />}
      {errorText && (
        <ErrorView
          location={{
            state: {
              error: errorText,
            },
          }}
        />
      )}
    </Fragment>
  );
};

const withAccountLoginByEmailMutation = graphql(accountLoginByEmailMutation, {
  props: ({ mutate }) => ({
    accountLoginByEmail: (emailAddress, code) =>
      mutate({
        variables: {
          input: {
            email_address: emailAddress,
            email_authentication_code: code,
            set_cookie: true,
          },
        },
      }),
  }),
});

const withAccountUpdateMutation = graphql(accountUpdateMutation, {
  props: ({ mutate }) => ({
    accountUpdate: account =>
      mutate({
        variables: {
          input: account,
        },
      }),
  }),
});

const withUser = graphql(MeQuery, {
  props: ({ data: { me } }) => ({
    user: me,
  }),
  skip: ownProps => !ownProps.authenticated,
});

const withAccountAddRolesMutation = graphql(accountAddRoles, {
  props: ({ mutate }) => ({
    addShutterbugRole: accountId =>
      mutate({
        variables: {
          input: {
            account_id: accountId,
            // note: ACCOUNT_ROLES enum contains lowercase, but API requires uppercase
            roles: [ACCOUNT_ROLES.SHUTTERBUG.toUpperCase()],
          },
        },
      }),
  }),
});

const withClientStoreSetMutation = graphql(clientStoreSetMutation, {
  props: ({ mutate }) => ({
    clientStoreSet: input =>
      mutate({
        variables: {
          input,
        },
      }),
  }),
});

const mapStateToProps = ({ auth }) => {
  return {
    authenticated: auth.authenticated,
  };
};

const mapDispatchToProps = {
  loginSuccess: loginSuccessAction,
};

EmailAuthLinkResolver.propTypes = propTypes;

export { EmailAuthLinkResolver as EmailAuthLinkResolverComponent };

export default compose(
  withRouter,
  withAccountLoginByEmailMutation,
  withAccountUpdateMutation,
  connect(mapStateToProps, mapDispatchToProps),
  withUser,
  withAccountAddRolesMutation,
  withClientStoreSetMutation,
)(EmailAuthLinkResolver);
